import React, { useEffect, useMemo, useRef, useState } from "react";
import { Flex, theme, Typography } from "antd";
import { useTranslate } from "@refinedev/core";
import {
  DOCUMENT_ACCEPT,
  matchesAccept,
  toDocumentFilePresentation,
  uploadFileKey,
} from "../../helpers/fileList";
import { PlaybookAddOnCard } from "../PlaybookAddOnCard";
import { PlaybookLock } from "../PlaybookLock";
import { OrderNumber } from "../OrderNumber.styles";
import { usePlaybookDraft } from "../../context/PlaybookDraftContext";
import { DocumentFile } from "./types";
import { AddDocumentButton } from "./documents/AddDocumentButton";
import { DocumentDropzone } from "./documents/DocumentDropzone";
import { DocumentFileRow } from "./documents/DocumentFileRow";
import { DocumentPreviewModal } from "./documents/DocumentPreviewModal";
import { CONFIG_ITEM } from "../../data/addons";

/**
 * Settings card for the experience's source documents. The document list is the
 * shared PlaybookDraft slice — the SAME list the Step-1 sidebar manages and the
 * exact set uploaded at creation — so files added here appear in Step 1 and
 * vice-versa.
 *
 * Split into two sections (MED-6970): Source files (used in project creation,
 * merged into `all_documents.pdf`) and Extras (knowledge-base-only, `hidden`).
 * A single document can't show a visibility icon — an experience always needs
 * a visible main doc — so the icon only appears once there are 2+ documents.
 *
 * "Documents" is a Settings item (not an add-on), so it stores outside the
 * add-on draft; documents are uploaded through project creation, not the add-on
 * PATCH cascade.
 */
export const DocumentsConfigCard: React.FC = () => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const {
    documents,
    hiddenDocKeys,
    addDocuments,
    removeDocument,
    toggleDocumentHidden,
  } = usePlaybookDraft();
  const [previewFile, setPreviewFile] = useState<DocumentFile | null>(null);
  const sourceFileInputRef = useRef<HTMLInputElement>(null);
  const extrasFileInputRef = useRef<HTMLInputElement>(null);

  const handleFileList = (
    list: FileList | null,
    opts?: { hidden?: boolean }
  ) => {
    if (!list || list.length === 0) return;
    // The file picker enforces `accept`, but drag-drop bypasses it — validate
    // dropped files against the same allowed types so both paths agree.
    const accepted = Array.from(list).filter((file) =>
      matchesAccept(file, DOCUMENT_ACCEPT)
    );
    if (accepted.length === 0) return;
    addDocuments(accepted, opts);
  };

  // Local preview blobs — one per file, so a doc can be previewed before it's
  // uploaded. Revoked whenever the file list changes or the card unmounts, so
  // previewing doesn't leak object URLs.
  const previewUrls = useMemo(() => {
    const map = new Map<string, string>();
    documents.forEach((file) => {
      if (file.originFileObj) {
        map.set(uploadFileKey(file), URL.createObjectURL(file.originFileObj));
      }
    });
    return map;
  }, [documents]);
  useEffect(
    () => () => previewUrls.forEach((url) => URL.revokeObjectURL(url)),
    [previewUrls]
  );

  const presentations = documents.map((file) => {
    const key = uploadFileKey(file);
    return toDocumentFilePresentation(
      file,
      hiddenDocKeys.has(key),
      previewUrls.get(key)
    );
  });
  const sourceDocs = presentations.filter((p) => !p.hidden);
  const extraDocs = presentations.filter((p) => p.hidden);

  const renderRow = (file: DocumentFile) => {
    // An experience always needs a visible main doc, so the icon is hidden
    // only for the last remaining visible doc — hiding it is a dead end.
    // An already-hidden doc always keeps its icon, so it can be unhidden.
    const showVisibilityToggle = file.hidden || sourceDocs.length > 1;
    return (
      <DocumentFileRow
        key={file.id}
        file={file}
        showVisibilityToggle={showVisibilityToggle}
        onPreview={() => setPreviewFile(file)}
        onToggleHidden={() => toggleDocumentHidden(file.id)}
        onRemove={() => removeDocument(file.id)}
      />
    );
  };

  return (
    <PlaybookAddOnCard
      itemId={CONFIG_ITEM.Documents}
      title={t("createExperience.addOns.documents.title")}
      description={t("createExperience.addOns.documents.description")}
    >
      {/* A playbook carries no documents at all — they are uploaded with the
          experience — so the whole card is inert in playbook-edit mode. */}
      <PlaybookLock variant="tab">
        <input
          ref={sourceFileInputRef}
          type="file"
          multiple
          accept={DOCUMENT_ACCEPT}
          style={{ display: "none" }}
          onChange={(e) => {
            handleFileList(e.target.files);
            e.target.value = "";
          }}
        />
        <input
          ref={extrasFileInputRef}
          type="file"
          multiple
          accept={DOCUMENT_ACCEPT}
          style={{ display: "none" }}
          onChange={(e) => {
            handleFileList(e.target.files, { hidden: true });
            e.target.value = "";
          }}
        />

        {documents.length === 0 ? (
          <DocumentDropzone
            onFiles={handleFileList}
            onBrowse={() => sourceFileInputRef.current?.click()}
          />
        ) : (
          <>
            <Typography.Text strong>
              {t("createExperience.addOns.documents.sourceFiles.title")}
            </Typography.Text>
            <Typography.Paragraph
              type="secondary"
              style={{ margin: "4px 0 16px", lineHeight: 1.45 }}
            >
              {sourceDocs.length === 0
                ? t("createExperience.addOns.documents.sourceFiles.emptyDesc")
                : t(
                    "createExperience.addOns.documents.sourceFiles.filesPower",
                    {
                      count: sourceDocs.length,
                    }
                  )}
            </Typography.Paragraph>

            <Flex vertical gap={10}>
              {sourceDocs.map((file, i) => (
                // Read-only order number — index 0 is the primary document that
                // drives the merge. Reordering stays in the Step-1 sidebar.
                <Flex key={file.id} align="center" gap={10}>
                  <OrderNumber $token={token} $isMain={i === 0}>
                    {i + 1}
                  </OrderNumber>
                  <div style={{ flex: 1, minWidth: 0 }}>{renderRow(file)}</div>
                </Flex>
              ))}
            </Flex>

            <AddDocumentButton
              label={t("createExperience.addOns.documents.addMore")}
              onClick={() => sourceFileInputRef.current?.click()}
            />

            <Typography.Text strong style={{ display: "block", marginTop: 24 }}>
              {t("createExperience.addOns.documents.extras.title")}
            </Typography.Text>
            <Typography.Paragraph
              type="secondary"
              style={{ margin: "4px 0 16px", lineHeight: 1.45 }}
            >
              {extraDocs.length === 0
                ? t("createExperience.addOns.documents.extras.emptyDesc")
                : t("createExperience.addOns.documents.extras.filesPower", {
                    count: extraDocs.length,
                  })}
            </Typography.Paragraph>

            {extraDocs.length > 0 ? (
              <Flex vertical gap={10}>
                {extraDocs.map((file) => renderRow(file))}
              </Flex>
            ) : null}

            <AddDocumentButton
              label={t("createExperience.addOns.documents.addMore")}
              onClick={() => extrasFileInputRef.current?.click()}
            />
          </>
        )}

        <DocumentPreviewModal
          file={previewFile}
          onClose={() => setPreviewFile(null)}
        />
      </PlaybookLock>
    </PlaybookAddOnCard>
  );
};

export default DocumentsConfigCard;
